import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, Firestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, FirebaseStorage, connectStorageEmulator } from 'firebase/storage';

// Firebase configuration - requires environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Enhanced configuration validation
const validateFirebaseConfig = () => {
  const requiredFields = [
    'REACT_APP_FIREBASE_API_KEY',
    'REACT_APP_FIREBASE_AUTH_DOMAIN', 
    'REACT_APP_FIREBASE_PROJECT_ID',
    'REACT_APP_FIREBASE_STORAGE_BUCKET',
    'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    'REACT_APP_FIREBASE_APP_ID'
  ];

  const missingFields = requiredFields.filter(field => !process.env[field]);
  
  if (missingFields.length > 0) {
    console.error('❌ Firebase configuration is incomplete!');
    console.error('Missing environment variables:', missingFields);
    console.error('Please check your .env file and ensure all Firebase variables are set.');
    console.error('Refer to env.example for the required format.');
    
    // Show helpful configuration info
    console.log('📋 Current configuration status:');
    requiredFields.forEach(field => {
      const value = process.env[field];
      console.log(`  ${field}: ${value ? '✅ Set' : '❌ Missing'}`);
    });
    
    throw new Error(`Firebase configuration incomplete. Missing: ${missingFields.join(', ')}`);
  }
  
  console.log('✅ All Firebase environment variables are configured');
};

// Validate configuration before initializing
try {
  validateFirebaseConfig();
} catch (error) {
  console.error('Firebase initialization failed:', error);
  // Don't throw here to allow the app to load with error handling
}

console.log('🔧 Firebase configuration:', {
  hasApiKey: !!firebaseConfig.apiKey,
  hasProjectId: !!firebaseConfig.projectId,
  hasAuthDomain: !!firebaseConfig.authDomain,
  hasStorageBucket: !!firebaseConfig.storageBucket,
  hasMessagingSenderId: !!firebaseConfig.messagingSenderId,
  hasAppId: !!firebaseConfig.appId,
  projectId: firebaseConfig.projectId,
  authDomain: firebaseConfig.authDomain,
  apiKeyPrefix: firebaseConfig.apiKey?.substring(0, 7) || 'none'
});

// Additional debugging for Google Auth
console.log('🔍 Google Auth Debug Info:', {
  currentDomain: window.location.hostname,
  isLocalhost: window.location.hostname === 'localhost',
  protocol: window.location.protocol,
  userAgent: navigator.userAgent.substring(0, 50) + '...'
});

let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let storage: FirebaseStorage;

try {
  // Initialize Firebase app
  app = initializeApp(firebaseConfig);
  console.log('✅ Firebase app initialized successfully');

  // Initialize Auth with enhanced configuration
  auth = getAuth(app);
  
  // Configure auth settings for better compatibility
  auth.useDeviceLanguage();
  
  // Connect to emulator in development if needed
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true') {
    try {
      connectAuthEmulator(auth, 'http://localhost:9098');
      console.log('🔧 Connected to Firebase Auth emulator');
    } catch (error) {
      console.log('ℹ️ Auth emulator not available, using production');
    }
  }
  
  console.log('✅ Firebase auth initialized successfully');

  // Initialize Firestore
  db = getFirestore(app);
  
  // Connect to Firestore emulator in development if needed
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true') {
    try {
      connectFirestoreEmulator(db, 'localhost', 8081);
      console.log('🔧 Connected to Firestore emulator');
    } catch (error) {
      console.log('ℹ️ Firestore emulator not available, using production');
    }
  }
  
  console.log('✅ Firebase Firestore initialized successfully');

  // Initialize Firebase Storage
  storage = getStorage(app);

  // Connect to Storage emulator in development if needed
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true') {
    try {
      connectStorageEmulator(storage, 'localhost', 9198);
      console.log('🔧 Connected to Firebase Storage emulator');
    } catch (error) {
      console.log('ℹ️ Storage emulator not available, using production');
    }
  }

  console.log('✅ Firebase Storage initialized successfully');

} catch (error) {
  console.error('❌ Firebase initialization failed:', error);
  console.error('Please check your Firebase configuration and try again.');
  
  // Create fallback objects to prevent app crashes
  app = {} as FirebaseApp;
  auth = {} as Auth;
  db = {} as Firestore;
  storage = {} as FirebaseStorage;
}

export { auth, db, storage }; 